(() => {
  // ---------- CONFIG ----------
  const TABS_ROOT_SELECTOR = 'variant-selects[id^="variant-selects-"], .product-tabs, [data-product-tabs-root]';
  const FORM_SELECTOR = '.product__info-container .main-product-form';
  const TAB_SELECTOR  = '.product-tab';
  const SELECTED_CLASS = 'is-selected';

  // ---------- RECHARGE ALERT VISIBILITY ----------
  function updateRechargeAlertVisibility(hasSellingPlan) {
    const subscriptionBlocks = document.querySelectorAll(
      '.product__block--recharge_alert[data-show-only-for-subscription="true"]'
    );
    const nonSubscriptionBlocks = document.querySelectorAll(
      '.product__block--recharge_alert[data-show-only-for-subscription="false"]'
    );

    if (hasSellingPlan) {
      subscriptionBlocks.forEach(b => { b.style.display = 'flex'; });
      nonSubscriptionBlocks.forEach(b => { b.style.display = 'none'; });
    } else {
      subscriptionBlocks.forEach(b => { b.style.display = 'none'; });
      nonSubscriptionBlocks.forEach(b => { b.style.display = 'flex'; });
    }
  }

  // ---------- AFTERPAY (SQUARE) MESSAGING VISIBILITY ----------
  // Afterpay installments apply to one-time purchases only. Hide the Square
  // placement when a subscription (selling plan) is selected; show it and sync
  // data-amount to the one-time price when the one-time tab is active.
  // opts is the selected tab's parsed data-options ({ total_price, ... }).
  function updateAfterpayPlacement(hasSellingPlan, opts) {
    const placements = document.querySelectorAll('.product__block--afterpay');
    if (!placements.length) return;

    placements.forEach(el => {
      if (hasSellingPlan) {
        el.style.display = 'none';
        return;
      }
      // total_price is money-formatted (e.g. "$49.99"); Afterpay wants a bare
      // decimal amount, so strip everything but digits and the decimal point.
      const amount = String((opts && opts.total_price) || '').replace(/[^0-9.]/g, '');
      if (amount) el.setAttribute('data-amount', amount);
      el.style.display = 'block';
    });
  }

  // ---------- INIT ONE SCOPE (idempotent) ----------
  function initProductTabLogic(scope) {
    if (!scope || scope.dataset.ptabsInitialized === 'true') return;

    const form =
      scope.querySelector(FORM_SELECTOR) ||
      scope.closest('.product, .quick-view, [id^="ProductInfo-"], .product_info-container')?.querySelector(FORM_SELECTOR) ||
      document.querySelector(FORM_SELECTOR);

    if (!form) return;

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;

    let quantityInput = form.querySelector('input[name="quantity"]');
    if (!quantityInput) {
      quantityInput = document.createElement('input');
      quantityInput.type  = 'number';
      quantityInput.name  = 'quantity';
      quantityInput.value = 1;
      quantityInput.min   = 1;
      form.appendChild(quantityInput);
    }

    // ---------- SELLING PLAN HELPERS ----------
    function getOrCreateSellingPlanInput() {
      let el = form.querySelector('input[name="selling_plan"]');
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = 'selling_plan';
        form.appendChild(el);
      }
      return el;
    }
    function removeSellingPlanInput() {
      form.querySelector('input[name="selling_plan"]')?.remove();
    }
    function setSellingPlanValue(value) {
      const el = getOrCreateSellingPlanInput();
      el.value = value;
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
    function clearSellingPlanValue() {
      removeSellingPlanInput();
      form.dispatchEvent(new Event('sellingplan:cleared', { bubbles: true }));
    }

    // ---------- CTA LABEL ----------
    const submitBtn =
      form.querySelector('button[type="submit"].product-form__submit') ||
      form.querySelector('.product-form__submit[type="submit"]')       ||
      form.querySelector('button[type="submit"]');
    const submitLabelEl   = submitBtn?.querySelector('.btn__text') || submitBtn || null;
    const originalCtaText = submitLabelEl ? submitLabelEl.textContent.trim() : 'Add to cart';

    function setSubmitLabel(text) {
      if (!submitLabelEl) return;
      submitLabelEl.textContent = (text && String(text).trim()) || originalCtaText;
    }

    // ---------- UTILS ----------
    function parseOptions(tab) {
      try { return JSON.parse(tab.getAttribute('data-options') || '{}'); }
      catch { return {}; }
    }

    // The quantity selector is rendered as a sibling of the .product-tab
    // buttons inside <variant-selects> — NOT inside any tab element.
    // All quantity lookups go through scope, never tab.querySelector().
    function getQSelector() {
      return scope.querySelector('.quantity-selector');
    }

    function updateQuantityClone(value) {
      const clone = scope.querySelector('.quantity-amount-clone');
      if (clone) clone.textContent = value;
    }

    function updateQuantityButtons(value) {
      const qs = getQSelector();
      if (!qs) return;

      const decBtn = qs.querySelector('.quantity-selector--button.decrement');
      const incBtn = qs.querySelector('.quantity-selector--button.increment');
      const qInput = qs.querySelector('.quantity-selector--input');
      if (!decBtn || !incBtn || !qInput) return;

      const min = parseInt(qInput.min || 1, 10);
      const max = parseInt(qInput.max || 10, 10);
      const val = parseInt(value, 10);

      const atMin = val <= min;
      const atMax = val >= max;

      // Visual active class
      decBtn.classList.toggle('active', !atMin);
      incBtn.classList.toggle('active', !atMax);

      // aria-disabled keeps buttons in tab order so AT users reach them
      // and hear "dimmed" rather than having them skipped silently
      decBtn.setAttribute('aria-disabled', atMin ? 'true' : 'false');
      incBtn.setAttribute('aria-disabled', atMax ? 'true' : 'false');

      // Boundary announcer — fires at min/max, clears in between
      const announcer = qs.querySelector('.quantity-selector--announcer');
      if (announcer) {
        announcer.textContent = atMin
          ? `Minimum quantity of ${min} reached`
          : atMax
          ? `Maximum quantity of ${max} reached`
          : '';
      }

      // Value announcer — fires on every change so AT hears the new
      // quantity without the user navigating back to the input
      const valueAnnouncer = qs.querySelector('.quantity-selector--value-announcer');
      if (valueAnnouncer) {
        valueAnnouncer.textContent = `Quantity: ${val}`;
      }
    }

    // ---------- SELECT TAB ----------
    function selectTab(tab) {
      // Deselect all — single pass
      scope.querySelectorAll(TAB_SELECTOR).forEach(t => {
        t.classList.remove(SELECTED_CLASS);
        t.setAttribute('aria-pressed', 'false');
      });

      // Activate the chosen tab
      tab.classList.add(SELECTED_CLASS);
      tab.setAttribute('aria-pressed', 'true');

      // Sync hidden variant id input
      const variantId = tab.getAttribute('data-variant-id');
      if (idInput && variantId && idInput.value !== variantId) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Selling plan + CTA label
      const opts = parseOptions(tab);
      const spId = opts?.selling_plan_id?.trim?.() || '';
      if (spId) setSellingPlanValue(spId); else clearSellingPlanValue();
      setSubmitLabel(opts?.cta_label);

      updateRechargeAlertVisibility(!!spId);
      updateAfterpayPlacement(!!spId, opts);

      // Show quantity selector only for OTP tabs.
      // Subscription tabs have a fixed quantity — hide the selector.
      const qs = getQSelector();
      if (qs) {
      const isOtp = !tab.classList.contains('has-selling-plan');

      if (isOtp) {
        qs.removeAttribute('inert');
        qs.removeAttribute('aria-hidden');
        qs.classList.remove('quantity-selector--disabled');

        const qInput = qs.querySelector('.quantity-selector--input');
        if (qInput) {
        quantityInput.value = qInput.value;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        updateQuantityClone(qInput.value);
        updateQuantityButtons(qInput.value);
      }
      } else {
        qs.setAttribute('inert', '');
        qs.setAttribute('aria-hidden', 'true');
        qs.classList.add('quantity-selector--disabled');
      }
      }
    }

    // ---------- EVENT LISTENERS ----------

    // Tab click — native <button> handles Enter/Space natively,
    // so this single delegated listener covers all input methods.
    scope.addEventListener('click', (e) => {
      const tab = e.target.closest(TAB_SELECTOR);
      if (tab && scope.contains(tab)) selectTab(tab);
    });

    // Quantity +/- button click.
    // The quantity selector is a sibling of the tabs inside scope,
    // so this delegated listener correctly reaches it.
    scope.addEventListener('click', (e) => {
      const dec = e.target.closest('.quantity-selector--button.decrement');
      const inc = e.target.closest('.quantity-selector--button.increment');
      if (!dec && !inc) return;

      // Honour aria-disabled — do not process if button is dimmed
      if ((dec || inc).getAttribute('aria-disabled') === 'true') return;

      const qInput = scope.querySelector('.quantity-selector--input');
      if (!qInput) return;

      const step    = inc ? 1 : -1;
      const current = parseInt(qInput.value, 10) || 1;
      const min     = parseInt(qInput.min    || 1, 10);
      const max     = parseInt(qInput.max    || 10, 10);
      const next    = Math.min(max, Math.max(min, current + step));

      qInput.value        = next;
      quantityInput.value = next;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      updateQuantityClone(next);
      updateQuantityButtons(next);
    });

    // Quantity input keyboard — block value-changing keys only so the
    // input is read-only to keyboard users without trapping focus.
    // Tab/Shift+Tab and all navigation keys pass through freely.
    scope.addEventListener('keydown', (e) => {
      if (!e.target.classList.contains('quantity-selector--input')) return;
      const blocked = ['ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'];
      if (blocked.includes(e.key)) e.preventDefault();
    });

    // Quantity input scroll — prevent scroll-wheel from changing the value.
    scope.addEventListener('wheel', (e) => {
      if (e.target.classList.contains('quantity-selector--input')) e.preventDefault();
    }, { passive: false });

    // Arrow key navigation between tab buttons.
    // Enter/Space are handled natively by <button> — no case needed.
    // Guard returns early if the event originated from the quantity input
    // so ArrowDown on the input never switches the purchase option.
    scope.addEventListener('keydown', (e) => {
      if (e.target.classList.contains('quantity-selector--input')) return;

      const tab = e.target.closest(TAB_SELECTOR);
      if (!tab || !scope.contains(tab)) return;

      const tabs = Array.from(scope.querySelectorAll(TAB_SELECTOR));
      const i    = tabs.indexOf(tab);

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight': {
          e.preventDefault();
          const next = tabs[(i + 1) % tabs.length];
          next.focus();
          selectTab(next);
          break;
        }
        case 'ArrowUp':
        case 'ArrowLeft': {
          e.preventDefault();
          const prev = tabs[(i - 1 + tabs.length) % tabs.length];
          prev.focus();
          selectTab(prev);
          break;
        }
      }
    });

    // ---------- INITIALIZE DEFAULT TAB ----------
    const initial =
      scope.querySelector(`${TAB_SELECTOR}[data-default="true"]`) ||
      scope.querySelector(TAB_SELECTOR);
    if (initial) selectTab(initial);

    scope.dataset.ptabsInitialized = 'true';
  }

  // ---------- SCAN & INIT ALL ROOTS ----------
  function scanAndInit(root = document) {
    root.querySelectorAll(TABS_ROOT_SELECTOR).forEach(initProductTabLogic);
  }

  // 1) Run once on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scanAndInit(document));
  } else {
    scanAndInit(document);
  }

  // 2) Hyper quick-view open event
  document.addEventListener('quick-view:open', (e) => {
    const modal = e.detail?.modal || document.querySelector('.quick-view, #QuickViewModal');
    if (modal) scanAndInit(modal);
  }, { passive: true });

  // 3) Shopify section load
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target || e.detail?.sectionElement || document;
    scanAndInit(container);
  }, { passive: true });

  // 4) Global MutationObserver — initialize whenever matching markup is injected
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(TABS_ROOT_SELECTOR)) {
          initProductTabLogic(node);
        } else if (node.querySelector) {
          const candidate = node.querySelector(TABS_ROOT_SELECTOR);
          if (candidate) initProductTabLogic(candidate);
        }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

})();